import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

// ── SANITIZE ──────────────────────────────────────────────────
export function sanitizePayload(data) {
  const DATE_FIELDS = ['dob','baptism_date','confirmation_date','death_date','wedding_date','joined_date'];
  const UUID_FIELDS = ['district_id','family_id','parent_id'];
  const out = { ...data };

  DATE_FIELDS.forEach(f => {
    if (out[f] === '' || out[f] === undefined) out[f] = null;
  });
  UUID_FIELDS.forEach(f => {
    if (out[f] === '' || out[f] === undefined) out[f] = null;
  });

  if (!out.phone || out.phone.trim() === '') out.phone = '';

  return out;
}

// ── READ ──────────────────────────────────────────────────────
export function useMembers(filters = {}) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    let q = supabase
      .from('members')
      .select(`
        *,
        districts:district_id (id, name),
        families:family_id (id, name),
        member_groups (role, groups (id, name, type))
      `)
      .order('status')
      .order('first_name');

    if (filters.status)          q = q.eq('status', filters.status);
    if (filters.district_id)     q = q.eq('district_id', filters.district_id);
    if (filters.membership_type) q = q.eq('membership_type', filters.membership_type);
    if (filters.family_id)       q = q.eq('family_id', filters.family_id);
    if (filters.search) {
      const s = filters.search;
      q = q.or(`first_name.ilike.%${s}%,last_name.ilike.%${s}%,phone.ilike.%${s}%,email.ilike.%${s}%,id_number.ilike.%${s}%,member_number.ilike.%${s}%`);
    }

    const { data, error } = await q;
    if (error) toast.error('Failed to load members: ' + error.message);
    setMembers(data || []);
    setLoading(false);
  }, [JSON.stringify(filters)]);

  useEffect(() => { fetch(); }, [fetch]);
  return { members, loading, refetch: fetch };
}

export function useMember(id) {
  const [member, setMember]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    supabase.from('members')
      .select(`
        *,
        districts:district_id (id, name),
        families:family_id (id, name),
        member_groups (role, groups (id, name, type)),
        contributions (*),
        attendance (type, service_id, sunday_services (date))
      `)
      .eq('id', id).single()
      .then(({ data }) => { setMember(data); setLoading(false); });
  }, [id]);

  return { member, loading };
}

export function useFamilyMembers(familyId) {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    if (!familyId) return;
    supabase.from('members')
      .select('id, first_name, last_name, family_role, membership_type, status, districts:district_id (name), phone')
      .eq('family_id', familyId)
      .then(({ data }) => setMembers(data || []));
  }, [familyId]);
  return members;
}

// ── WRITE ─────────────────────────────────────────────────────
export async function createMember(payload) {
  const clean = sanitizePayload(payload);
  const { data, error } = await supabase.from('members').insert(clean).select().single();
  if (error) {
    if (error.message.includes('members_phone_unique')) {
      toast.error('A member with this phone number already exists.');
    } else if (error.message.includes('members_email_unique')) {
      toast.error('A member with this email already exists.');
    } else if (error.message.includes('members_id_number_unique')) {
      toast.error('A member with this ID number already exists.');
    } else {
      toast.error(error.message);
    }
    return null;
  }
  toast.success('Member added ✅');
  return data;
}

export async function updateMember(id, payload) {
  const clean = sanitizePayload(payload);
  const { error } = await supabase
    .from('members')
    .update({ ...clean, updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) {
    if (error.message.includes('members_phone_unique')) {
      toast.error('Another member already has this phone number.');
    } else if (error.message.includes('members_email_unique')) {
      toast.error('Another member already has this email.');
    } else if (error.message.includes('members_id_number_unique')) {
      toast.error('A member with this ID number already exists.');
    } else {
      toast.error(error.message);
    }
    return null;
  }
  toast.success('Member updated ✅');
  return { id };   // return anything truthy – the caller only checks if truthy
}

export async function deleteMember(id) {
  const { error } = await supabase.from('members').delete().eq('id', id);
  if (error) { toast.error(error.message); return false; }
  toast.success('Member removed');
  return true;
}

export async function syncMemberGroups(memberId, groupIds = []) {
  await supabase.from('member_groups').delete().eq('member_id', memberId);
  if (!groupIds.length) return true;
  const { error } = await supabase.from('member_groups')
    .insert(groupIds.map(group_id => ({ member_id: memberId, group_id })));
  if (error) { toast.error('Failed to sync groups'); return false; }
  return true;
}

// ── REFERENCE DATA ────────────────────────────────────────────
export function useDistricts() {
  const [districts, setDistricts] = useState([]);
  useEffect(() => {
    supabase.from('districts').select('*').order('name')
      .then(({ data }) => setDistricts(data || []));
  }, []);
  return districts;
}

export function useFamilies() {
  const [families, setFamilies] = useState([]);
  const fetch = async () => {
    const { data } = await supabase.from('families')
      .select('*, districts:district_id (name)')
      .order('name');
    setFamilies(data || []);
  };
  useEffect(() => { fetch(); }, []);
  return { families, refetch: fetch };
}

export async function createFamily(name, districtId) {
  const { data, error } = await supabase.from('families')
    .insert({ name, district_id: districtId || null })
    .select().single();
  if (error) { toast.error(error.message); return null; }
  toast.success('Family household created');
  return data;
}

export function useGroups() {
  const [groups, setGroups] = useState([]);
  useEffect(() => {
    supabase.from('groups').select('*').order('name')
      .then(({ data }) => setGroups(data || []));
  }, []);
  return groups;
}